/**
 * ErrorBanner Component
 * A reusable banner for displaying errors, warnings, info messages, and success messages
 * Designed to be placed at the top of pages/modals for consistent error display
 */

import React from 'react';
import { AlertTriangle, Info, CheckCircle, X } from 'lucide-react';
import './ErrorBanner.css';

const ErrorBanner = ({
  type = 'error', // 'error', 'warning', 'info', 'success'
  message = null,
  messages = [], // Array of error messages
  onDismiss = null,
  className = ''
}) => {
  // If no message or messages, don't render anything
  if (!message && (!messages || messages.length === 0)) {
    return null;
  }

  // Convert single message to array for unified rendering
  const errorList = message ? [message] : messages;

  const icons = {
    error: <AlertTriangle size={20} />,
    warning: <AlertTriangle size={20} />,
    info: <Info size={20} />,
    success: <CheckCircle size={20} />
  };

  const titles = {
    error: 'Error',
    warning: 'Warning',
    info: 'Information',
    success: 'Success'
  };

  return (
    <div className={`error-banner error-banner-${type} ${className}`} role="alert">
      <div className="error-banner-content">
        <div className="error-banner-icon">
          {icons[type]}
        </div>
        <div className="error-banner-text">
          <div className="error-banner-title">{titles[type]}</div>
          {errorList.length === 1 ? (
            <p className="error-banner-message">{errorList[0]}</p>
          ) : (
            <ul className="error-banner-list">
              {errorList.map((msg, index) => (
                <li key={index}>{msg}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {onDismiss && (
        <button
          className="error-banner-dismiss"
          onClick={onDismiss}
          aria-label="Dismiss"
          type="button"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
};

export default ErrorBanner;
